import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  formatCurrency, 
  getCurrencySymbol, 
  getCurrencyCode,
  formatDate,
  formatNumber,
  getRegionalDivisions,
  getRegionalDivisionLabel,
  countryUsesStates,
  getTimeFormat,
  parseCurrency
} from '../utilities/helpers/countryUtils';
import { getCountryConfig, DEFAULT_COUNTRY, isValidCountryCode } from '../utilities/constants/countryConfig';

// Create the context
const CountryContext = createContext();

// Custom hook to use the country context
export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

// Helper function to get country from localStorage
const getCountryFromStorage = () => {
  try {
    const storedCountry = localStorage.getItem('selectedCountry');
    if (storedCountry && isValidCountryCode(storedCountry.toUpperCase())) {
      return storedCountry.toUpperCase();
    }
  } catch (error) {
    console.warn('Error reading country from localStorage:', error);
  }
  return DEFAULT_COUNTRY;
};

// Helper function to save country to localStorage
const saveCountryToStorage = (countryCode) => {
  try {
    localStorage.setItem('selectedCountry', countryCode);
  } catch (error) {
    console.warn('Error saving country to localStorage:', error);
  }
};

// Country Provider component
export const CountryProvider = ({ children }) => {
  const [countryCode, setCountryCode] = useState(() => getCountryFromStorage());
  const [countryConfig, setCountryConfig] = useState(() => getCountryConfig(getCountryFromStorage()));

  // Initialize country from localStorage on mount
  useEffect(() => {
    const storedCountry = getCountryFromStorage();
    if (storedCountry !== countryCode) {
      setCountryCode(storedCountry);
      setCountryConfig(getCountryConfig(storedCountry));
    }
  }, []);

  // Utility functions that use the current country context
  const contextUtils = {
    // Currency utilities
    formatCurrency: (amount, options = {}) => formatCurrency(amount, countryCode, options),
    getCurrencySymbol: () => getCurrencySymbol(countryCode),
    getCurrencyCode: () => getCurrencyCode(countryCode),
    parseCurrency: (currencyString) => parseCurrency(currencyString, countryCode),
    
    // Date and number formatting
    formatDate: (date) => formatDate(date, countryCode),
    formatNumber: (number, decimalPlaces = 2) => formatNumber(number, countryCode, decimalPlaces),
    
    // Regional utilities
    getRegionalDivisions: () => getRegionalDivisions(countryCode),
    getRegionalDivisionLabel: () => getRegionalDivisionLabel(countryCode),
    countryUsesStates: () => countryUsesStates(countryCode),
    
    // Time format
    getTimeFormat: () => getTimeFormat(countryCode),
    
    // Manual country change (if needed)
    setCountry: (newCountryCode) => {
      if (newCountryCode !== countryCode) {
        setCountryCode(newCountryCode);
        setCountryConfig(getCountryConfig(newCountryCode));
        saveCountryToStorage(newCountryCode);
      }
    }
  };

  const contextValue = {
    // Current country information
    countryCode,
    countryConfig,
    countryName: countryConfig.name,
    
    // Currency information
    currency: countryConfig.currency,
    currencySymbol: countryConfig.currency.symbol,
    currencyCode: countryConfig.currency.code,
    
    // Formatting information
    dateFormat: countryConfig.dateFormat,
    timeFormat: countryConfig.timeFormat,
    numberFormat: countryConfig.numberFormat,
    
    // Field configuration
    fields: countryConfig.fields,
    
    // Utility functions
    ...contextUtils
  };

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};

// HOC for components that need country context
export const withCountry = (Component) => {
  return function CountryWrappedComponent(props) {
    const countryContext = useCountry();
    return <Component {...props} country={countryContext} />;
  };
};

export default CountryContext;